import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToolbar,
  AlertController,
  ModalController,
} from '@ionic/angular/standalone';

import { ComponentAccess } from '../../../../../common/decorators/component-access.decorator';
import { Capability } from '../../../../../common/enums/capability.enum';
import { ProFeature } from '../../../../../common/enums/pro-feature-message.enum';
import { ProGateService } from '../../../../core/payment/services/pro-gate.service';
import { DbService } from '../../services/db.service';
import { SyncService } from '../../sync/sync.service';
import { AppConfigService } from '../../../../../common/config/app.config.service';
import { LandingPagePath } from '../../../../../common/enums/legal-url.enum';
import { PublishMessage } from '../../../../../common/enums/publish.enum';
import { LogFactory } from '../../../../../common/services/logger/log-factory';
import {
  BACKUP_FIXABLE,
  errorCodeOf,
  ListingItemType,
  PublishErrorCode,
  PublishTerms,
  PublishingApiService,
  serverMessageOf,
} from '../../services/publishing-api.service';

/** What the sheet hands back when the publisher confirms. */
export interface PublishResult {
  published: boolean;
  asDraft: boolean;
}

/**
 * The publish sheet: name it, categorise it, accept the agreement, send it for
 * review.
 *
 * The agreement is rendered from what the server returns and never composed
 * here — the wording is evidence of the rights the publisher granted, and the
 * version that was shown is sent back so the acceptance records it (ADR-0225).
 * That is the same rule the purchase-consent component follows.
 *
 * A draft skips the agreement, because a draft is not published to anyone; the
 * agreement is asked for when it is submitted.
 */
@Component({
  selector: 'app-publish-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonInput,
    IonTextarea,
    IonCheckbox,
    IonNote,
    IonSpinner,
  ],
  templateUrl: './publish-modal.component.html',
  styleUrls: ['./publish-modal.component.scss'],
})
@ComponentAccess('PublishModalComponent')
export class PublishModalComponent implements OnInit {
  /** The library item being published. */
  @Input({ required: true }) itemType!: ListingItemType;
  @Input({ required: true }) itemId!: string;
  /** Prefills the title so the common case is one tap. */
  @Input() itemName = '';
  /** What a draft already said, when reopening one. Prefilled over the
   *  item-derived defaults so a re-save edits the draft rather than replacing
   *  what the publisher wrote with the item's name again. */
  @Input() draftTitle?: string;
  @Input() draftDescription?: string;
  /** Set when submitting a draft that already exists: the sheet patches that
   *  listing instead of creating a second one (only one listing per item). */
  @Input() listingId?: string;

  private readonly modalCtrl = inject(ModalController);
  private readonly alertCtrl = inject(AlertController);
  private readonly proGate = inject(ProGateService);
  private readonly sync = inject(SyncService);
  private readonly api = inject(PublishingApiService);
  private readonly appConfig = inject(AppConfigService);
  private readonly db = inject(DbService);
  private readonly logger = inject(LogFactory).createLogger(PublishModalComponent);

  protected readonly Copy = PublishMessage;

  protected readonly title = signal('');
  protected readonly description = signal('');
  protected readonly accepted = signal(false);
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);
  /** Shown instead of the ordinary busy state while the backup step runs. */
  protected readonly backingUp = signal(false);
  /** One backup offer per attempt: a refusal that survives the backup is real. */
  private retrying = false;

  /** Null until the agreement loads; publishing stays disabled until it does. */
  protected readonly terms = signal<PublishTerms | null>(null);

  async ngOnInit(): Promise<void> {
    this.title.set(this.draftTitle ?? this.itemName);
    this.description.set(this.draftDescription ?? '');
    try {
      this.terms.set(await this.api.publishTerms());
    } catch (err) {
      // Without the agreement there is nothing to accept, so publishing stays
      // closed rather than proceeding without recorded consent.
      this.logger.error('Publishing agreement failed to load', err);
      this.error.set(PublishMessage.AgreementLoadFailed);
    }
  }

  protected agreementUrl(): string {
    return this.appConfig.landingPageUrl(LandingPagePath.PublisherAgreement);
  }

  protected get submittingDraft(): boolean {
    return !!this.listingId;
  }

  protected canPublish(): boolean {
    return !!this.terms() && this.accepted() && !!this.title().trim() && !this.busy();
  }

  protected cancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  protected saveDraft(): Promise<void> {
    return this.send(true);
  }

  protected publish(): Promise<void> {
    return this.send(false);
  }

  private async send(asDraft: boolean): Promise<void> {
    if (this.busy()) return;
    if (!asDraft && !this.canPublish()) return;
    this.busy.set(true);
    this.error.set(null);
    try {
      const fields = {
        title: this.title().trim() || undefined,
        description: this.description().trim() || undefined,
      };
      const consent = asDraft
        ? {}
        : { consentGiven: true, consentVersion: this.terms()!.version };
      if (this.listingId) {
        // Saving a draft again is saving, not submitting: only a publish moves
        // it into review. Sending the status unconditionally meant re-saving a
        // draft silently sent it off for review.
        await this.api.update(this.listingId, {
          ...fields,
          ...consent,
          ...(asDraft ? {} : { status: 'PENDING_REVIEW' as const }),
        });
      } else {
        await this.api.publish({
          itemType: this.itemType,
          itemId: this.itemId,
          ...fields,
          asDraft,
          ...consent,
        });
      }
      const result: PublishResult = { published: !asDraft, asDraft };
      await this.modalCtrl.dismiss(result, 'confirm');
    } catch (err) {
      // "It is not backed up" is not a dead end — it is the one refusal the app
      // can act on itself. Offer to back up and publish rather than sending the
      // publisher away to do it and come back.
      if (await this.isBackupFixable(err)) {
        await this.offerBackupThenRetry(asDraft, this.messageFor(err));
        return;
      }
      // Every other refusal states the single thing to fix (no cover, foreign
      // content), so show that rather than a generic failure.
      this.logger.error('Publish failed', err);
      this.error.set(this.messageFor(err));
    } finally {
      this.busy.set(false);
    }
  }

  /**
   * Whether a backup would actually change the answer.
   *
   * The two "not backed up" codes always would. NO_COVER_IMAGE usually would
   * not — but it does when the cover exists HERE and not yet in the cloud: the
   * server reads the backed-up copy of the item, so a cover added after the
   * last backup is invisible to it. That case told the publisher to add a cover
   * image they had just added, and refused to offer the one action that would
   * have fixed it, so they could not get out of the loop at all.
   */
  private async isBackupFixable(err: unknown): Promise<boolean> {
    const code = errorCodeOf(err) as PublishErrorCode;
    if (BACKUP_FIXABLE.includes(code)) return true;
    if (code !== PublishErrorCode.NO_COVER_IMAGE) return false;
    return this.hasLocalCover();
  }

  /** Does the item on THIS device carry a cover? */
  private async hasLocalCover(): Promise<boolean> {
    try {
      const item =
        this.itemType === 'PROGRAM'
          ? await this.db.getProgram(this.itemId)
          : await this.db.getSequence(this.itemId);
      return !!item?.imageMediaId;
    } catch (err) {
      // Unreadable locally: fall back to the server's answer rather than
      // offering a backup that may not help.
      this.logger.debug('Could not read the local cover state', err, { itemId: this.itemId });
      return false;
    }
  }

  /**
   * Offer to back up, then publish — the two steps in the order they have to
   * happen, without making the publisher leave and come back.
   *
   * Backing up is a Pro feature in its own right, and having LISTINGS does not
   * imply having CLOUD_SYNC (an admin grants each per plan), so the paywall is
   * checked before anything is offered. Declining stops, leaving the server's
   * own explanation on screen so it is clear why nothing was published.
   *
   * The retry is what decides the outcome, not the backup: a backup can finish
   * without having carried this particular item, and the server is the only
   * thing that actually knows. `retrying` stops a second refusal from offering
   * the same backup again.
   */
  private async offerBackupThenRetry(asDraft: boolean, refusal: string): Promise<void> {
    if (this.retrying) {
      this.error.set(refusal);
      return;
    }
    if (!this.proGate.requireCapability(Capability.CLOUD_SYNC, ProFeature.CloudSync)) {
      this.error.set(refusal);
      return;
    }

    const confirmed = await this.confirmBackup();
    if (!confirmed) {
      this.error.set(refusal);
      return;
    }

    this.retrying = true;
    this.busy.set(true);
    this.backingUp.set(true);
    try {
      await this.sync.syncNow();
    } catch (err) {
      this.logger.error('Backup before publishing failed', err);
      this.error.set(PublishMessage.BackupFailed);
      return;
    } finally {
      this.backingUp.set(false);
      this.busy.set(false);
    }

    await this.send(asDraft);
  }

  private async confirmBackup(): Promise<boolean> {
    const alert = await this.alertCtrl.create({
      header: PublishMessage.BackupNeededHeader,
      message: PublishMessage.BackupNeededMessage,
      buttons: [
        { text: PublishMessage.BackupNeededCancel, role: 'cancel' },
        { text: PublishMessage.BackupNeededConfirm, role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'confirm';
  }

  private messageFor(err: unknown): string {
    return serverMessageOf(err) ?? PublishMessage.PublishFailed;
  }
}
